import { CommonModule } from '@angular/common';
import { Component, ElementRef, inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject, takeUntil } from 'rxjs';
import { RestService } from '../../../core/services/rest.service';
import {
  ImportValidationGroup,
  toImportValidationView,
} from '../../../core/utils/import-validation-error.util';
import { ImportValidationErrorPanelComponent } from '../import-validation-error-panel/import-validation-error-panel.component';

export interface ImportAddToSuiteResult {
  testSuiteId: string;
  testSuiteName: string;
  isNewSuite: boolean;
}

@Component({
  selector: 'app-import-add-to-suite-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, ImportValidationErrorPanelComponent],
  templateUrl: './import-add-to-suite-modal.component.html',
  styleUrl: './import-add-to-suite-modal.component.scss',
})
export class ImportAddToSuiteModalComponent implements OnInit, OnDestroy {
  activeModal = inject(NgbActiveModal);

  @Input() projectId = '';
  @Input() connectionId = '';
  /** 'xlsx' → 'EXCEL', 'csv' → 'CSV', 'zephyr' → 'ZEPHYR_ESSENTIAL' */
  @Input() documentType = 'xlsx';
  /** The selected test-case rows to persist. */
  @Input() selectedTestCases: any[] = [];
  /** When true, closes with form data and lets the parent call persistTestCase. */
  @Input() persistMode = false;

  // ── Close button hover state (mirrors other modal components) ────────────
  isActive = false;

  // ── Toggle state ──────────────────────────────────────────────────────────
  isNewSuite = true;
  isExistingSuite = false;

  // ── Form fields ───────────────────────────────────────────────────────────
  newSuiteName = '';
  selectedSuiteId = '';

  // ── Existing suites list ──────────────────────────────────────────────────
  existingSuites: any[] = [];
  isLoadingSuites = false;

  // ── Save state ────────────────────────────────────────────────────────────
  isSaving = false;
  saveError = '';
  validationErrors: ImportValidationGroup[] = [];
  validationSummary = '';
  duplicateNote = '';

  @ViewChild('modalBody') private modalBody?: ElementRef<HTMLElement>;
  @ViewChild('errorPanel') private errorPanel?: ElementRef<HTMLElement>;

  private errorScrollTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly destroy$ = new Subject<void>();

  constructor(private readonly restService: RestService) {}

  ngOnInit(): void {
    this.loadExistingSuites();
  }

  ngOnDestroy(): void {
    if (this.errorScrollTimer) {
      clearTimeout(this.errorScrollTimer);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Derived helpers ───────────────────────────────────────────────────────

  get origin(): string {
    if (this.documentType === 'csv') {
      return 'CSV';
    }
    if (this.documentType === 'zephyr') {
      return 'ZEPHYR_ESSENTIAL';
    }
    return 'EXCEL';
  }

  get canAdd(): boolean {
    if (this.isSaving) return false;
    if (this.isNewSuite) return this.newSuiteName.trim().length > 0;
    if (this.isExistingSuite) return !!this.selectedSuiteId;
    return false;
  }

  // ── Toggle handlers (mutually exclusive) ─────────────────────────────────

  onToggleNew(): void {
    this.isNewSuite = !this.isNewSuite;
    if (this.isNewSuite) {
      this.isExistingSuite = false;
    }
    this.clearSaveError();
  }

  onToggleExisting(): void {
    this.isExistingSuite = !this.isExistingSuite;
    if (this.isExistingSuite) {
      this.isNewSuite = false;
    }
    this.clearSaveError();
  }

  // ── Actions ───────────────────────────────────────────────────────────────

  onCancel(): void {
    this.activeModal.dismiss('cancel');
  }

  onAdd(): void {
    if (!this.canAdd) return;

    if (this.persistMode) {
      const result: ImportAddToSuiteResult = this.isNewSuite
        ? { testSuiteId: '', testSuiteName: this.newSuiteName.trim(), isNewSuite: true }
        : {
            testSuiteId: this.selectedSuiteId,
            testSuiteName: this.getSuiteName(this.selectedSuiteId),
            isNewSuite: false,
          };
      this.activeModal.close(result);
      return;
    }

    this.isSaving = true;
    this.clearSaveError();

    if (this.isNewSuite) {
      this.createAndSave();
    } else {
      this.saveToSuite(this.selectedSuiteId, this.getSuiteName(this.selectedSuiteId), false);
    }
  }

  // ── Private helpers ───────────────────────────────────────────────────────

  private loadExistingSuites(): void {
    if (!this.projectId) return;
    this.isLoadingSuites = true;
    this.restService
      .getTestCaseVersions(this.projectId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          const raw = res?.data ?? res ?? [];
          this.existingSuites = Array.isArray(raw) ? raw : [];
          this.isLoadingSuites = false;
        },
        error: () => {
          this.existingSuites = [];
          this.isLoadingSuites = false;
        },
      });
  }

  private createAndSave(): void {
    this.restService
      .createTcgTestSuite(this.projectId, this.newSuiteName.trim())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          const suiteId: string =
            res?.data?.id ?? res?.data?._id ?? res?.id ?? res?._id ?? '';
          if (!suiteId) {
            this.isSaving = false;
            this.applySaveError(null, 'Failed to create test suite. Please try again.');
            return;
          }
          this.saveToSuite(suiteId, this.newSuiteName.trim(), true);
        },
        error: (err: unknown) => {
          this.isSaving = false;
          this.applySaveError(err, 'Failed to create test suite.');
        },
      });
  }

  private saveToSuite(suiteId: string, suiteName: string, isNew: boolean): void {
    this.restService
      .addImportedTestCasesToTestSuite(
        this.projectId,
        suiteId,
        this.origin,
        this.connectionId,
        this.selectedTestCases
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isSaving = false;
          const result: ImportAddToSuiteResult = { testSuiteId: suiteId, testSuiteName: suiteName, isNewSuite: isNew };
          this.activeModal.close(result);
        },
        error: (err: unknown) => {
          this.isSaving = false;
          this.applySaveError(err, 'Failed to save test cases.');
        },
      });
  }

  private applySaveError(err: unknown, fallback: string): void {
    const view = toImportValidationView(err, fallback);
    this.validationErrors = view.groups;
    this.validationSummary = view.summary;
    this.duplicateNote = view.duplicateNote;
    this.saveError = view.genericMessage;
    this.scrollErrorsIntoView();
  }

  private scrollErrorsIntoView(): void {
    if (!this.validationErrors.length && !this.saveError) {
      return;
    }
    if (this.errorScrollTimer) {
      clearTimeout(this.errorScrollTimer);
    }
    this.errorScrollTimer = setTimeout(() => {
      const body = this.modalBody?.nativeElement;
      const target = this.errorPanel?.nativeElement;
      if (!body || !target) {
        return;
      }
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const top =
        target.getBoundingClientRect().top -
        body.getBoundingClientRect().top +
        body.scrollTop;
      body.scrollTo({
        top: Math.max(0, top),
        behavior: reducedMotion ? 'auto' : 'smooth',
      });
    });
  }

  private clearSaveError(): void {
    this.saveError = '';
    this.validationErrors = [];
    this.validationSummary = '';
    this.duplicateNote = '';
  }

  private getSuiteName(suiteId: string): string {
    const suite = this.existingSuites.find(
      (s: any) => (s.id ?? s._id) === suiteId
    );
    return suite?.testSuiteName ?? suite?.versionName ?? suiteId;
  }
}
